export interface AuthError {
  type: 'validation' | 'authentication' | 'network' | 'server' | 'unknown';
  message: string;
  userMessage: string;
}

export class AuthErrorHandler {
  static createError(error: any, context: string = 'login'): AuthError {
    // Handle network errors
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      return {
        type: 'network',
        message: error.message,
        userMessage: 'Network error. Please check your connection and try again.'
      };
    }

    // Handle request cancellation
    if (error.name === 'AbortError') {
      return {
        type: 'network',
        message: error.message,
        userMessage: 'Request was cancelled. Please try again.'
      };
    }

    // Handle HTTP status errors
    if (error.message) {
      const message = error.message.toLowerCase();
      
      // Account not found
      if (message.includes('account not found') || message.includes('user not found')) {
        return {
          type: 'authentication',
          message: error.message,
          userMessage: 'No account found with this email address. Please check your email or contact support.'
        };
      }

      // Invalid credentials
      if (message.includes('invalid email or password') || message.includes('invalid credentials')) {
        return {
          type: 'authentication',
          message: error.message,
          userMessage: 'Invalid email or password. Please check your credentials and try again.'
        };
      }

      // Account disabled/suspended
      if (message.includes('disabled') || message.includes('suspended') || message.includes('inactive')) {
        return {
          type: 'authentication',
          message: error.message,
          userMessage: 'Your account has been disabled. Please contact support for assistance.'
        };
      }

      // Rate limiting
      if (message.includes('too many') || message.includes('rate limit')) {
        return {
          type: 'authentication',
          message: error.message,
          userMessage: 'Too many login attempts. Please wait a few minutes before trying again.'
        };
      }

      // Server errors
      if (message.includes('server error') || message.includes('internal error')) {
        return {
          type: 'server',
          message: error.message,
          userMessage: 'Server error. Please try again later or contact support if the problem persists.'
        };
      }

      // Authentication required
      if (message.includes('authentication required')) {
        return {
          type: 'authentication',
          message: error.message,
          userMessage: 'Your session has expired. Please log in again.'
        };
      }
    }

    // Default error
    return {
      type: 'unknown',
      message: error.message || 'Unknown error occurred',
      userMessage: 'An unexpected error occurred. Please try again or contact support if the problem persists.'
    };
  }

  static getValidationError(field: string, value: string): AuthError | null {
    if (!value || !value.trim()) {
      return {
        type: 'validation',
        message: `${field} is required`,
        userMessage: `${field} is required`
      };
    }

    if (field === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value.trim())) {
        return {
          type: 'validation',
          message: 'Invalid email format',
          userMessage: 'Please enter a valid email address'
        };
      }
    }

    if (field === 'password' && value.length < 1) {
      return {
        type: 'validation',
        message: 'Password is required',
        userMessage: 'Password is required'
      };
    }

    return null;
  }

  static isRetryableError(error: AuthError): boolean {
    return error.type === 'network' || error.type === 'server';
  }

  static getRetryMessage(error: AuthError): string {
    if (error.type === 'network') {
      return 'This appears to be a network issue. Please check your connection and try again.';
    }
    
    if (error.type === 'server') {
      return 'This appears to be a temporary server issue. Please try again in a few moments.';
    }
    
    return '';
  }
}
